import { ReactNode } from 'react'
import { NavLink, Link } from 'react-router-dom'

const DISCORD_URL =
  import.meta.env.VITE_DISCORD_URL || 'https://discord.gg/R4udP6TbM2'

interface Props {
  children: ReactNode
  active: 'home' | 'why' | 'how' | 'pricing'
  showBottomNav?: boolean
  plain?: boolean
}

function LogoMark() {
  return (
    <svg
      className="v2-logo-mark"
      viewBox="0 0 320 180"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="92" cy="90" r="44" stroke="currentColor" strokeWidth="22" fill="none" />
      <path d="M165 136L205 44" stroke="currentColor" strokeWidth="22" strokeLinecap="square" />
    </svg>
  )
}

const NAV_ITEMS: Array<
  | { key: Props['active']; to: string; label: string }
  | { key: Props['active']; href: string; label: string }
> = [
  { key: 'home',    to: '/',         label: 'Home' },
  { key: 'how',     href: '/#how',   label: 'How' },
  { key: 'pricing', to: '/pricing',  label: 'Pricing' },
]

/**
 * Shared chrome for every v2 page:
 *   - animated stage lights (halation)
 *   - top-right Join Discord link
 *   - bottom-center nav
 * Film grain + edge vignette come from body::before / body::after in CSS.
 */
export default function StageLayout({ children, active, showBottomNav = true, plain = false }: Props) {
  return (
    <>
      <div className="v2-stage-lights" aria-hidden="true" />

      <Link to="/" className="v2-logo-link" aria-label="OpenDeploy — Home">
        <LogoMark />
        <span className="v2-logo-wordmark" aria-hidden="true">opendeploy</span>
      </Link>

      <a
        className="v2-corner-link"
        href={DISCORD_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        Join Discord
      </a>

      <main className={`v2-stage${plain ? ' v2-stage-plain' : ''}`}>{children}</main>

      {showBottomNav && (
        <nav className="v2-bottom-nav" aria-label="Primary">
          {NAV_ITEMS.map((item) =>
            'href' in item ? (
              <a
                key={item.key}
                href={item.href}
                className={item.key === active ? 'active' : undefined}
              >
                {item.label}
              </a>
            ) : (
              <NavLink
                key={item.key}
                to={item.to}
                className={item.key === active ? 'active' : undefined}
              >
                {item.label}
              </NavLink>
            ),
          )}
        </nav>
      )}
    </>
  )
}
